import { ValueOf } from '@snack-uikit/utils';
import { merge } from 'lodash';

export const RESPONSE_TYPE = {
  ArrayBuffer: 'arraybuffer',
  Blob: 'blob',
  Document: 'document',
  JSON: 'json',
  Text: 'text',
  Stream: 'stream',
  Basic: 'basic',
} as const;

type ResponseType = ValueOf<typeof RESPONSE_TYPE>;

export type RequestParams<BodyType = unknown> = {
  url: string;
  body?: BodyType;
  signal?: RequestInit['signal'];
  params?: Record<string, unknown>;
  headers?: HeadersInit;
  disableHeaders?: string[];
  onUploadProgress?(progress: ProgressEvent): void;
  responseType?: ResponseType;
  onError?(error: Error): void;
  onResponse?(response: RestClientResponse): void;
  skipAuthorization?: boolean;
  skipErrorHandling?: boolean;
};

export type RestClientResponse<Data = unknown> = {
  data: Data;
  status: number;
  statusText: string;
  headers: Headers;
  config: RequestParams;
  response?: Response;
};

export type RestClientConfig = {
  baseURL?: string;
  headers?: HeadersInit;
  responseType?: ResponseType;
  onError?(error: Error): void;
  onResponse?(response: RestClientResponse): void;
  authorization?: {
    getToken(): Promise<string | undefined>;
  };
};

export class RestClient {
  private config: RestClientConfig;

  constructor(config: RestClientConfig) {
    this.config = config;
  }

  private async request<Data, BodyType = unknown>(
    method: string,
    params: RequestParams<BodyType>,
  ): Promise<RestClientResponse<Data>> {
    const {
      url,
      body,
      signal,
      params: queryParams,
      responseType,
      headers,
      onError,
      onResponse,
      skipAuthorization,
      skipErrorHandling,
    } = params;
    const combinedHeaders = new Headers({ ...this.config.headers, ...headers });

    if (!skipAuthorization && this.config.authorization) {
      const token = await this.config.authorization.getToken();
      if (token) {
        combinedHeaders.set('Authorization', `Bearer ${token}`);
      }
    }

    const query = queryParams
      ? '?' +
        new URLSearchParams(queryParams as Record<string, string>).toString()
      : '';
    const fullUrl = `${this.config.baseURL || ''}${url}${query}`;

    const response = await fetch(fullUrl, {
      method,
      headers: combinedHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    const errorHandler = onError || this.config.onError;
    const responseHandler = onResponse || this.config.onResponse;

    if (!response.ok && !skipErrorHandling && errorHandler) {
      errorHandler(new Error(response.statusText));
    }

    const data = await this.parseResponse(
      response,
      responseType || this.config.responseType,
    );

    const restClientResponse: RestClientResponse<Data> = {
      data: data as Data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: params,
      response,
    };

    if (responseHandler) {
      responseHandler(restClientResponse);
    }

    return restClientResponse;
  }

  get<Data>(params: RequestParams): Promise<RestClientResponse<Data>> {
    return this.request<Data>('GET', params);
  }

  post<Data, BodyType = unknown>(
    params: RequestParams<BodyType>,
  ): Promise<RestClientResponse<Data>> {
    return this.request<Data, BodyType>('POST', params);
  }

  put<Data, BodyType = unknown>(
    params: RequestParams<BodyType>,
  ): Promise<RestClientResponse<Data>> {
    return this.request<Data, BodyType>('PUT', params);
  }

  delete<Data>(params: RequestParams): Promise<RestClientResponse<Data>> {
    return this.request<Data>('DELETE', params);
  }

  patch<Data, BodyType = unknown>(
    params: RequestParams<BodyType>,
  ): Promise<RestClientResponse<Data>> {
    return this.request<Data, BodyType>('PATCH', params);
  }

  private async parseResponse(
    response: Response,
    responseType: ResponseType = RESPONSE_TYPE.JSON,
  ): Promise<unknown> {
    switch (responseType) {
      case RESPONSE_TYPE.Text:
        return response.text();
      case RESPONSE_TYPE.Blob:
        return response.blob();
      case RESPONSE_TYPE.ArrayBuffer:
        return response.arrayBuffer();
      case RESPONSE_TYPE.JSON:
      default:
        return response.json();
    }
  }
}

// Интерфейс для конфигурации RestClientBuilder
export interface RestClientBuilderConfig extends RestClientConfig {
  getRequiredParams?(): Record<string, unknown>;
}

export interface IRestClientBuilder {
  setCustomConfig(
    customRestClientConfig: Omit<RestClientBuilderConfig, 'getRequiredParams'>,
  ): void;
  createInstance(config?: RestClientConfig): RestClient;
}

export class RestClientBuilder implements IRestClientBuilder {
  private readonly _defaultRestClientConfig: RestClientBuilderConfig = {};
  private _customRestClientConfig: Omit<
    RestClientBuilderConfig,
    'getRequiredParams'
  > = {};
  private authorizationToken: string | undefined = undefined;

  constructor(defaultRestClientConfig: RestClientBuilderConfig = {}) {
    this._defaultRestClientConfig = defaultRestClientConfig;
  }

  setCustomConfig(
    customRestClientConfig: Omit<RestClientBuilderConfig, 'getRequiredParams'>,
  ) {
    this._customRestClientConfig = customRestClientConfig;
  }

  private mergeConfigs(instanceConfig?: RestClientConfig): RestClientConfig {
    return merge(
      {},
      this._defaultRestClientConfig,
      this._customRestClientConfig,
      instanceConfig,
    );
  }

  createInstance(config?: RestClientConfig): RestClient {
    const mergedConfig = this.mergeConfigs(config);

    return new RestClient(mergedConfig);
  }
}
